import type {
  AssetPlacementType,
  FundinfoPricingFrequency,
  GetInstrumentCountriesStatusResponseOne,
  InstrumentBrokerStatus,
  InstrumentMutualFundLegalStatus,
  StructuredProductType,
} from '@Common/types/bo/instruments';
import type { LegalEntity } from '@Common/types/bo/users/users';
import type {
  B2bInstrumentAssetType,
  InstrumentAssetType,
  InstrumentManagementType,
  InstrumentMutualFundAssetClass,
  SharesStatus,
} from '@Common/types/instruments/instrument';

import { isBoExternal } from '@sh4res/bo-common/utils/env';

const countriesStatusFields: Record<keyof GetInstrumentCountriesStatusResponseOne, string> = {
  id: 'Code pays',
  name: 'Nom',
  entity: 'Entité',
  shares_status: 'Status Shares',
  instrument_id: 'ID Instrument',
};

const countriesStatusEntities: Record<LegalEntity, string> = {
  sal: 'SAL',
  sfa: 'SFA',
  sda: 'SDA',
};

const countriesStatusSharesStatus: Record<SharesStatus, string> = {
  disabled: 'Désactivé',
  sell_only: 'Vente uniquement',
  suspended: 'Suspendu',
  tradable: 'Négociable',
};

export const instrumentsTranslations = {
  instruments: {
    title: 'Instruments',
    name: isBoExternal() ? 'Fonds |||| Liste des fonds' : 'Instrument |||| Instruments',
    tabs: {
      details: 'Détails',
      documents: 'Documents',
      cutoff_dates: 'Dates de cutoff',
      schedule: 'Planification',
    },
    fields: {
      id: 'ID Instrument',
      symbol: 'Symbole',
      name: 'Nom',
      exchange: 'Bourse',
      status: 'Status Courtier',
      class: 'Classe',
      tradable: 'Négociable',
      fractionable: 'Fractionnable',
      about: 'À propos (fournisseur)',
      about_en: "À propos (remplacement de l'anglais)",
      about_fr: 'À propos (français)',
      about_pl: 'À propos (polonais)',
      short_name: 'Libellé',
      short_description: 'Description courte',
      is_enabled: 'Activé',
      created_at: 'Créé à',
      from_created_at: 'Créé après',
      to_created_at: 'Créé avant',
      reason: 'Raison de la modification',
      logo_path: 'Logo',
      isin: 'Code ISIN',
      is_price_fetched: 'Prix récupérés',
      country_codes_tradable: 'Pays négociable',
      country_codes_sell_only: 'Pays vente uniquement',
      country_codes_suspended: 'Pays suspension',
      country_codes_disabled: 'Pays désactivés',
      risk: 'Niveau de risque',
      mutual_fund_asset_class: 'Classe d’actifs',
      management_company: 'Société de gestion',
      kid_urls: 'URL DIC',
      prospectus_url: 'URL Prospectus',
      management_fee_pct: 'Frais de gestion (%)',
      management_type: 'Type de gestion',
      five_year_beta: 'Bêta 5 ans',
      five_year_beta_fallback: 'Bêta 5 ans (de secours)',
      is_adr: 'Est un ADR',
      shares_ca_wht_rate_at_source: 'Taux de retenue à la source (CA)',
      type: 'Type',
      country_code: 'Code pays',
      is_halted: 'Suspendu',
      support_code: 'Code d’assistance',
      asset_type: 'Type d’actif',
      settlement_location: 'Lieu de règlement',
      sub_custodian_account_number: 'N° compte sous-conservateur',
      has_pending_orders: 'Ordres en attente',
      is_complex: 'Instrument complexe',
      is_ftt_rate_zero_override: 'Taux FTT défini à 0',
      intermediate_instrument_parent_id:
        'ID de l’instrument référencé (distribution intermédiaire CA Upvest)',
      has_open_positions: 'Positions ouvertes',
      data_fetch_completed: 'Récupération de données terminée',
      country_settings: 'Paramètres pays',
      is_pea_eligible: 'Éligible au PEA',
      legal_status: 'Statut juridique',
      is_etf: 'Est un ETF',
      is_triggered_arbitrage_allowed: 'Arbitrage déclenché autorisé',
      is_single_underlying_asset: 'Est un actif sous-jacent unique',
      is_withdrawal_trigger_threshold_enabled: 'Est un seuil de déclenchement de retrait autorisé',
      is_floor_nav_enabled: 'Est un NAV plancher autorisé',
      is_floor_price_enabled: 'Est un prix plancher autorisé',
      marketing_date_end_at: 'Date de fin de commercialisation',
      marketing_date_start_at: 'Date de début de commercialisation',
      maturity_date: "Date d'échéance",
      last_nav_date: 'Dernière date de VL',
      last_nav: 'Dernière VL',
      nav_cut_off_buy: 'Heure limite de réception TCC Buy',
      nav_cut_off_sell: 'Heure limite de réception TCC Sell',
      admin_cut_off_buy: "Heure d'envoi Oddo Buy",
      admin_cut_off_sell: "Heure d'envoi Oddo Sell",
      announcement_delay_buy: "Délai d'annonce Buy (jours ouvrés)",
      announcement_delay_sell: "Délai d'annonce Sell (jours ouvrés)",
      pricing_frequency: 'Fréquence de valorisation',
      performance: 'Performance',
      issue_date_at: 'Issue date',
      strike_date_at: 'Strike date',
      structured_product_type: 'Structured product type',
      issue_price_denomination_rate: 'Denomination rate',
      wm_company_ids: 'WM Companies IDs',
      issue_price_denomination_value: 'Denomination value',
      minimum_trading_size: 'Minimum trading size',
      placement_type: 'Type de placement',
      commission_to_third_parties_percentage_rate: 'Commission aux tiers (%)',
      one_off_entry_cost_percentage_rate: 'Frais d’entrée uniques (%)',
      running_cost_percentage_rate: 'Coût de fonctionnement (%)',
      custom_execution_flow: "Flow d'exécution spécifique",
      entry_fees_non_acquired_percentage_rate: 'Taux de frais d’entrée non aquis maximum (%)',
      rebate_fee_rate: 'Taux de frais de remise (%)',
      gross_ongoing_costs_ex_ante_rate: 'Taux ex ante des coûts courants bruts (%)',
      transaction_costs_ex_ante_rate: 'Taux ex ante des coûts de transaction (%)',
      incidental_costs_ex_ante_rate: 'Taux ex ante des coûts accessoires (%)',
      ccb_last_vl_date: 'Dernière date de valorisation',
      has_close_price: 'Prix de clôture disponible',
      qty_decimal_places: 'Décimales de quantité',
      redemption_period_start_at: 'Début de période de rachat',
      redemption_period_end_at: 'Fin de période de rachat',
      maturity_target_instrument_id: 'Instrument cible à l’échéance',
    },
    tooltips: {
      qty_decimal_places:
        'Le nombre maximum de décimales autorisées pour les quantités fractionnées de cet instrument. Cette valeur doit correspondre exactement à la précision acceptée par le courtier.',
      nav_cut_off:
        "Heure avant laquelle le TCC doit recevoir un ordre pour qu'il soit considéré comme reçu le jour même. Ce n'est pas le cut-off du dépositaire / PER assurantiel.",
      admin_cut_off:
        "Heure d'envoi du batch à Oddo. C'est aussi la date limite de validation des ordres de ce batch.",
      announcement_delay:
        "Nombre de jours ouvrés entre le jour de réception de l'ordre (heure limite de réception TCC) et le jour de son annonce à Oddo. 0 l'annonce le jour de réception.",
    },
    mutual_fund_asset_class: {
      diversified: 'Diversifié',
      equity: 'Actions',
      bond: 'Obligations',
      money_market: 'Monétaire',
      real_estate: 'Immobilier',
    } satisfies Record<InstrumentMutualFundAssetClass, string>,
    status: {
      active: 'Actif',
      inactive: 'Inactif',
    } satisfies Record<InstrumentBrokerStatus, string>,
    pricing_frequency: {
      daily: 'Quotidienne',
      weekly: 'Hebdomadaire',
      biweekly: 'Bimensuelle',
      monthly: 'Mensuelle',
      quarterly: 'Trimestrielle',
      semi_annual: 'Semestrielle',
      annual: 'Annuelle',
      semi_monthly: 'Semi-mensuelle',
      bi_monthly: 'Bimestrielle',
      ongoing: 'Continue',
    } satisfies Record<FundinfoPricingFrequency, string>,
    legal_status: {
      FCPE: 'FCPE',
      FCP: 'FCP',
      SICAV: 'SICAV',
      SPPICAV: 'SPPICAV',
      MONETAIRE: 'MONETAIRE',
      SICAF: 'SICAF',
      FCIMT: 'FCIMT',
      PIF: 'FIP',
      FCPI: 'FCPI',
      CIPF: 'FCPE',
      structured_products: 'Produits Structurés',
      euro_fund_life_insurance_scheme: 'Fonds euros en Assurance Vie',
      FCPR: 'FCPR',
      unit_trust: 'unit_trust',
      OEIC: 'OEIC',
      ICVC: 'ICVC',
      OPCI: 'OPCI',
      scpi_real_estate: 'SCPI',
      ICAV: 'ICAV',
      FPVG: 'FPVG',
      FIAR: 'FIAR',
      FPCI: 'FPCI',
      open_ended_umbrella_unit_trust: 'open_ended_umbrella_unit_trust',
      cayman_ltd: 'cayman_ltd',
      LLC: 'LLC',
      SCI: 'SCI',
      FPS: 'FPS',
      FIC: 'FIC',
      OFS: 'OFS',
      FFS: 'FFS',
      SCA: 'SCA',
      FCC: 'FCC',
      SCP: 'SCP',
      ELTIF: 'ELTIF',
      internal_fund: 'Fonds Interne',
      SC: 'SC',
      ETC: 'ETC',
      FIP: 'FIP',
      EMTN: 'EMTN',
      AD: 'AD',
      ACTION: 'ACTION',
      DIVERS: 'DIVERS',
    } satisfies Record<InstrumentMutualFundLegalStatus, string>,
    management_type: {
      active: 'Active',
      passive: 'Passive',
    } satisfies Record<InstrumentManagementType, string>,
    asset_type: {
      stock: 'Action',
      crypto: 'Crypto-monnaie',
      etf: 'ETF',
      'mutual-fund': 'Fonds commun de placement',
      'euro-fund': 'Fonds en euros',
      commodity: 'Matière première',
      eltif: 'ELTIF',
      'structured-product': 'Produit structuré',
      ccb: 'CCB',
    } satisfies Record<InstrumentAssetType | B2bInstrumentAssetType, string>,
    performance: {
      position_3m: '3 mois',
      position_6m: '6 mois',
      position_1y: '1 an',
      position_3y: '3 ans',
    },
    performance_short: {
      position_3m: '3M',
      position_6m: '6M',
      position_1y: '1A',
      position_3y: '3A',
    },
    structured_product_type: {
      autocall: 'Autocall',
      phoenix: 'Phoenix',
      reverse_convertible: 'Reverse convertible',
      capital_guaranteed: 'Capital guaranteed',
      cln: 'CLN',
      amc: 'AMC',
      other: 'Other',
    } satisfies Record<StructuredProductType, string>,
    placement_type: {
      public: 'Public',
      private: 'Privé',
    } satisfies Record<AssetPlacementType, string>,
    actions: {
      save: {
        title: 'Edit instrument',
        success: 'Instrument successfully saved.',
        failure: 'Error while saving instrument: %{error}',
        failure_logo: 'Error while saving logo: %{error}',
      },
      drop_logo_file: 'Drop your file here',
      confirmation: {
        enabled: 'Are you sure you want to enable this instrument ?',
        metadata_not_fetched: 'Instrument metadata has not been fetched yet or it is incomplete',
        no_beta: 'Instrument does not have a 5 year Beta value',
        disabled: 'Are you sure you want to disable this instrument ?',
        fetch:
          'Are you sure you want change Data Fetch status ? It can take up to 24 hours to be fetched.',
        default: 'Are you sure you want to edit this instrument ?',
      },
    },
    sections: {
      statuses: 'Status',
      documents: 'Documents',
      other: 'Autre',
      cut_offs: 'Cut-offs',
    },
    cutoff_dates: {
      title: 'Dates de cutoff',
      empty: 'Aucune date de cutoff',
      labels: {
        cutoff_date: 'Date de cutoff',
      },
      modal: {
        title: 'Modifier le calendrier de cutoff',
        add_label: 'Nouvelle date de cutoff',
        add: 'Ajouter',
        staged_additions: 'Dates à ajouter lors de l’enregistrement',
        min_one_hint:
          'Les dates passées ne peuvent pas être modifiées et au moins une date à venir doit être conservée.',
        success: 'Dates de cutoff mises à jour avec succès.',
        failure: 'Échec de la mise à jour des dates de cutoff : %{error}',
      },
    },
    documents: {
      kid: 'DIC',
      prospectus: 'Prospectus',
      complexity_and_target_market_assessment: 'Évaluation de la complexité et du marché cible',
      brochure: 'Brochure',
      term_sheet: "Lettre d'intention",
      empty: 'Aucun document de ce type',
      not_available: 'Aucun document disponible pour ce type d’instrument',
      create: {
        title: 'Ajouter un document',
        button: 'Ajouter un document',
        success: 'Document ajouté avec succès.',
        failure: "Erreur lors de l'ajout du document",
        choices: {
          kid: 'DIC',
          prospectus: 'Prospectus',
          complexity_and_target_market_assessment: 'Évaluation de la complexité et du marché cible',
          brochure: 'Brochure',
          term_sheet: "Lettre d'intention",
        },
      },
      labels: {
        is_archived: 'Archivé',
        created_at: 'Créé à',
      },
    },
    create: {
      title: 'Créer un instrument',
      steps: {
        1: 'Détails généraux',
        2: 'Spécifique',
        3: 'Disponibilité',
      },
      sections: {
        details: 'Détails',
        logo: 'Logo',
        structured_products: 'Détails du produit structuré',
        marketing_period: 'Période de commercialisation',
        trading_details: 'Détails de négociation',
        availability: 'Disponibilité',
      },
      actions: {
        success: 'Instrument créé avec succès.',
        failure: "Erreur lors de la création de l'instrument : %{error}",
      },
      labels: {
        add_wm_companies: 'Ajouter des entreprises Shares Pro',
      },
    },
  },

  'instruments/countries_status': {
    title: 'Country Settings',
    set_all: 'Set all shares status to',
    fields: countriesStatusFields,
    entities: countriesStatusEntities,
    shares_status: countriesStatusSharesStatus,
  },

  'instruments/documents': {
    delete_confirm: {
      title: 'Delete document',
      content: 'Are you sure you want to delete this document? This action cannot be undone.',
    },
    notifications: {
      deleted: 'Document successfully deleted.',
    },
  },

  'instruments/scheduled_edits': {
    title: 'Modifications planifiées',
    empty: 'Aucune modification planifiée pour cet instrument',
    fields: {
      id: 'ID',
      instrument_id: 'ID Instrument',
      type: 'Type',
      scheduled_at: 'Planifié à',
      status: 'Statut',
      created_at: 'Créé à',
      updated_at: 'Mis à jour à',
      actions: 'Actions',
    },
    types: {
      mark_suspendable: 'Marquer comme suspendu',
      mark_tradable: 'Marquer comme négociable',
    },
    statuses: {
      pending: 'En attente',
      triggered: 'Déclenché',
      cancelled: 'Annulé',
    },
    create: {
      title: 'Planifier une modification d’instrument',
      button: 'Planifier une modification',
      success: 'Modification planifiée créée',
      failure: 'Impossible de créer la modification planifiée : %{error}',
    },
    cancel: {
      button: 'Annuler',
      title: 'Annuler la modification planifiée',
      content: 'Cette modification planifiée sera annulée et ne sera pas exécutée.',
      success: 'Modification planifiée annulée',
      failure: 'Impossible d’annuler la modification planifiée : %{error}',
    },
  },
};
